<script setup lang="ts">
import { computed, ref, watch } from "vue";

type PoolItem = {
  items_id: number;
  code: string;
  name: string;
  sales_uom: string;
  sales_uom_qty: number;
  on_hand: number;
};

type FreePool = {
  promo_uid: string;
  promo_name: string;
  free_total_qty: number;
  free_uom_id: number | null;
  items: PoolItem[];
  expense_account_id: number | null;
};

type ChosenLine = { promo_uid: string; items_id: number; qty: number };

const props = defineProps<{
  pool: FreePool;
  // Existing override the cashier picked previously for this same promo
  // (e.g. modal re-opened to tweak the choice) — used to pre-fill qty
  // inputs instead of always resetting to the auto-pick.
  preselected?: ChosenLine[];
}>();

const emit = defineEmits<{
  (e: "apply", lines: ChosenLine[]): void;
  (e: "close"): void;
}>();

// Per-item cap in display UoM: how many of THIS item the cashier can
// actually give based on warehouse stock. Sales-UoM-qty translates the
// base inventory `on_hand` into the modal's display unit (typically CTN).
// We floor to whole units — partial cartons aren't a thing for the
// wholesale workflow this POS supports. Always intersect with the
// promo's free_total_qty so a high-stock item still can't exceed the
// promo's earned allotment.
const maxQtyFor = (item: PoolItem): number => {
  const packing = item.sales_uom_qty || 1;
  const inUom = Math.floor((Number(item.on_hand) || 0) / packing);
  return Math.max(0, Math.min(props.pool.free_total_qty, inUom));
};

// One qty input per pool item, keyed by items_id. Starts at 0 unless this
// item was preselected — then we honour the cashier's prior pick.
const qtyByItem = ref<Record<number, number>>({});

watch(
  () => props.pool,
  (pool) => {
    qtyByItem.value = {};
    if (!pool) return;
    for (const it of pool.items) qtyByItem.value[it.items_id] = 0;
    // Seed with prior selection if any; else distribute greedily across
    // items respecting each item's stock cap so we don't pre-fill an
    // out-of-stock item. The cashier can redistribute before saving.
    const seeded = (props.preselected ?? []).filter(
      (p) => p.promo_uid === pool.promo_uid
    );
    if (seeded.length > 0) {
      for (const s of seeded) {
        if (qtyByItem.value[s.items_id] !== undefined) {
          qtyByItem.value[s.items_id] = s.qty;
        }
      }
    } else {
      let remaining = pool.free_total_qty;
      for (const it of pool.items) {
        if (remaining <= 0) break;
        const cap = maxQtyFor(it);
        const take = Math.min(remaining, cap);
        if (take > 0) {
          qtyByItem.value[it.items_id] = take;
          remaining -= take;
        }
      }
    }
  },
  { immediate: true }
);

const totalSelected = computed(() =>
  Object.values(qtyByItem.value).reduce((s, q) => s + (Number(q) || 0), 0)
);

// "Should not exceed" semantics — the cashier MAY give fewer than the cart
// earned (customer declined, partial, etc.) but never more. Save enables
// when at least one unit is picked AND the total stays within the cap.
const valid = computed(
  () =>
    totalSelected.value > 0 &&
    totalSelected.value <= props.pool.free_total_qty + 0.0001
);

const overCap = computed(
  () => totalSelected.value > props.pool.free_total_qty + 0.0001
);

// Whether any pool item has positive stock — if not, the modal can't be
// confirmed at all and we show a clear "cancel to skip" message instead
// of the default "pick at least one" hint that would mislead the cashier.
const anyInStock = computed(() =>
  props.pool.items.some((it) => maxQtyFor(it) > 0)
);

const remaining = computed(
  () => Math.max(0, props.pool.free_total_qty - totalSelected.value)
);

// Snap the active row down to whichever cap binds first: this item's
// stock-derived max, or the remaining allotment after accounting for
// what's already on the other rows.
const onQtyInput = (itemsId: number) => {
  const item = props.pool.items.find((i) => i.items_id === itemsId);
  if (!item) return;
  const itemCap = maxQtyFor(item);
  const total = props.pool.free_total_qty;
  const others = Object.entries(qtyByItem.value)
    .filter(([k]) => Number(k) !== itemsId)
    .reduce((s, [, q]) => s + (Number(q) || 0), 0);
  const remainingCap = Math.max(0, total - others);
  const cap = Math.min(itemCap, remainingCap);
  if (qtyByItem.value[itemsId] > cap) qtyByItem.value[itemsId] = cap;
  if (qtyByItem.value[itemsId] < 0) qtyByItem.value[itemsId] = 0;
};

const handleSave = () => {
  if (!valid.value) return;
  const lines: ChosenLine[] = Object.entries(qtyByItem.value)
    .filter(([, q]) => Number(q) > 0)
    .map(([items_id, q]) => ({
      promo_uid: props.pool.promo_uid,
      items_id: Number(items_id),
      qty: Number(q),
    }));
  emit("apply", lines);
};
</script>

<template>
  <div
    class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
  >
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
          Choose Free Items
        </h3>
        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {{ pool.promo_name }} — pick which items to give the customer. Up
          to
          <span class="font-semibold">{{ pool.free_total_qty }}</span>
          free unit(s) in total.
        </p>
      </div>

      <div class="p-6 space-y-3 max-h-[60vh] overflow-y-auto">
        <div
          v-for="it in pool.items"
          :key="it.items_id"
          class="grid grid-cols-12 items-center gap-3 border-b border-gray-100 dark:border-gray-700 pb-3 last:border-b-0"
          :class="maxQtyFor(it) <= 0 ? 'opacity-60' : ''"
        >
          <div class="col-span-7">
            <div class="text-sm font-medium text-gray-900 dark:text-gray-100">
              {{ it.name }}
            </div>
            <div class="text-xs text-gray-500 dark:text-gray-400">
              {{ it.code }} · {{ it.sales_uom || "—" }} · stock
              <span
                :class="
                  maxQtyFor(it) > 0
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-red-600 dark:text-red-400'
                "
                >{{ it.on_hand }}</span
              >
              <span
                v-if="maxQtyFor(it) <= 0"
                class="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"
              >
                Out of stock
              </span>
              <span
                v-else
                class="ml-2 text-[11px] text-gray-400 dark:text-gray-500"
              >
                max {{ maxQtyFor(it) }} {{ it.sales_uom || "" }}
              </span>
            </div>
          </div>
          <div class="col-span-5">
            <input
              type="number"
              :min="0"
              :max="maxQtyFor(it)"
              :readonly="maxQtyFor(it) <= 0"
              step="0.01"
              v-model.number="qtyByItem[it.items_id]"
              @input="onQtyInput(it.items_id)"
              class="w-full px-3 py-2 text-right border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
              :class="
                maxQtyFor(it) <= 0
                  ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed'
                  : ''
              "
            />
          </div>
        </div>
      </div>

      <div class="px-6 py-3 bg-gray-50 dark:bg-gray-700/50 text-sm">
        <div class="flex justify-between">
          <span class="text-gray-600 dark:text-gray-400">Selected</span>
          <span
            class="font-semibold"
            :class="
              overCap
                ? 'text-red-600 dark:text-red-400'
                : 'text-emerald-600 dark:text-emerald-400'
            "
            >{{ totalSelected }} / {{ pool.free_total_qty }} max</span
          >
        </div>
        <div
          v-if="overCap"
          class="mt-1 text-xs text-red-600 dark:text-red-400"
        >
          Exceeds the eligible quantity. Reduce by
          {{ (totalSelected - pool.free_total_qty).toFixed(2) }}.
        </div>
        <div
          v-else-if="remaining > 0 && totalSelected > 0"
          class="mt-1 text-xs text-gray-500 dark:text-gray-400"
        >
          {{ remaining }} free unit(s) still available — give fewer if the
          customer doesn't want them all.
        </div>
        <div
          v-else-if="totalSelected === 0 && anyInStock"
          class="mt-1 text-xs text-gray-500 dark:text-gray-400"
        >
          Pick at least one free item before confirming.
        </div>
        <div
          v-else-if="totalSelected === 0 && !anyInStock"
          class="mt-1 text-xs text-red-600 dark:text-red-400"
        >
          No eligible items are in stock — cancel to skip the freebie.
        </div>
      </div>

      <div
        class="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 rounded-b-lg flex justify-end gap-3"
      >
        <button
          @click="$emit('close')"
          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          @click="handleSave"
          :disabled="!valid"
          class="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Confirm Free Items
        </button>
      </div>
    </div>
  </div>
</template>
